import { User } from 'firebase/auth';
import { API_URL } from '../config';

export interface SlackIntegration {
  webhookUrl: string;
  label: string;
  active: boolean;
  connectedAt: string | null;
}

/** Fetch the Slack integration for a project, or null if none is configured. */
export async function getSlackIntegration(
  user: User,
  projectId: string,
): Promise<SlackIntegration | null> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/slack/${projectId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to load Slack integration' }));
    throw new Error((body as { error?: string }).error ?? 'Failed to load Slack integration');
  }
  return res.json() as Promise<SlackIntegration>;
}

/** Save (create or replace) the Slack integration for a project. */
export async function saveSlackIntegration(
  user: User,
  projectId: string,
  webhookUrl: string,
  label = '',
): Promise<SlackIntegration> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/slack/${projectId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ webhookUrl, label }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to save Slack integration' }));
    throw new Error((body as { error?: string }).error ?? 'Failed to save Slack integration');
  }
  return res.json() as Promise<SlackIntegration>;
}

/** Remove the Slack integration for a project. */
export async function deleteSlackIntegration(user: User, projectId: string): Promise<void> {
  const token = await user.getIdToken();
  const res = await fetch(`${API_URL}/slack/${projectId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Failed to remove Slack integration' }));
    throw new Error((body as { error?: string }).error ?? 'Failed to remove Slack integration');
  }
}
